import { useState, useRef, useEffect } from "react";
import { Sparkles, Loader2, X, ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "@/lib/router-compat";
import { toast } from "sonner";

interface Props {
  targetPath?: string;
  placeholder?: string;
  compact?: boolean;
}

const ADVANCED_FILTER_TO_PARAM: Record<string, string> = {
  make: "make", model: "model",
  yearMin: "yearMin", yearMax: "yearMax",
  priceMin: "priceMin", priceMax: "priceMax",
  mileageMax: "mileageMax",
  fuelType: "fuelType", transmission: "transmission", bodyType: "bodyType",
  driveType: "driveType",
  powerMin: "powerMin", powerMax: "powerMax",
  locationCity: "locationCity", radiusKm: "radiusKm",
  sellerType: "sellerType", keywords: "keywords",
};

const VEHICLES_FILTER_TO_PARAM: Record<string, string> = {
  ...ADVANCED_FILTER_TO_PARAM,
  fuelType: "fuel",
  locationCity: "city",
};

// Static suggestion prompts — helps first-time users
const SUGGESTIONS = [
  "SUV diesel automat sub 20.000€",
  "BMW Seria 3 din 2018, sub 100.000 km",
  "Mașină de familie, 7 locuri, benzină",
  "Break diesel sub 15.000€, Cluj + 50 km",
  "Prima mașină, ieftină, cutie manuală",
  "Electric sau hibrid, sub 25.000€",
  "Audi sau BMW break, 2019+, automată",
  "Dacia Duster 4x4, sub 12.000€",
];

// Chip key → filter key(s) to remove when user clicks X
const CHIP_TO_FILTERS: Record<string, string[]> = {
  make: ["make", "model"],
  model: ["model"],
  year: ["yearMin", "yearMax"],
  yearMin: ["yearMin"],
  yearMax: ["yearMax"],
  price: ["priceMin", "priceMax"],
  priceMin: ["priceMin"],
  priceMax: ["priceMax"],
  mileageMax: ["mileageMax"],
  fuelType: ["fuelType"],
  transmission: ["transmission"],
  bodyType: ["bodyType"],
  driveType: ["driveType"],
  powerMin: ["powerMin"],
  powerMax: ["powerMax"],
  location: ["locationCity", "radiusKm"],
  sellerType: ["sellerType"],
  keywords: ["keywords"],
};

interface ParseResult {
  filters: Record<string, unknown>;
  labels: { key: string; label: string }[];
  count: number;
  relaxed: { droppedKey: string; droppedLabel: string; newCount: number } | null;
}

export const NLSearchBar = ({ targetPath = "/cauta", placeholder, compact }: Props) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ParseResult | null>(null);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // Close suggestions when clicking outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const runParse = async (q: string) => {
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("search-nl-to-filters", {
        body: { query: q },
      });
      if (error) throw error;
      const parsed: ParseResult = {
        filters: data?.filters ?? {},
        labels: data?.labels ?? [],
        count: data?.count ?? 0,
        relaxed: data?.relaxed ?? null,
      };
      if (Object.keys(parsed.filters).length === 0) {
        toast.info("Nu am putut deduce filtre. Încearcă mai specific — ex: „SUV diesel sub 20.000€”.");
        return;
      }
      setResult(parsed);
      setFocused(false);
    } catch (e) {
      console.error("[NLSearch] failed:", e);
      toast.error("Căutarea AI e temporar indisponibilă. Încearcă filtrele manuale.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const q = query.trim();
    if (!q) return;
    void runParse(q);
  };

  const goToResults = (filters: Record<string, unknown>) => {
    const params = new URLSearchParams();
    const paramMap = targetPath.startsWith("/vehicles") ? VEHICLES_FILTER_TO_PARAM : ADVANCED_FILTER_TO_PARAM;
    for (const [key, val] of Object.entries(filters)) {
      const paramKey = paramMap[key];
      if (!paramKey || val == null || val === "") continue;
      if (Array.isArray(val)) {
        if (val.length === 0) continue;
        params.set(paramKey, val.join(","));
      } else {
        params.set(paramKey, String(val));
      }
    }
    params.set("nlq", query.trim());
    // AI count is computed across all countries → ensure results page matches
    params.set("search_all_countries", "true");
    navigate(`${targetPath}?${params.toString()}`);
  };

  const removeChip = (chipKey: string) => {
    if (!result) return;
    const toRemove = CHIP_TO_FILTERS[chipKey] ?? [chipKey];
    const nextFilters = { ...result.filters };
    for (const k of toRemove) delete nextFilters[k];
    const nextLabels = result.labels.filter((l) => l.key !== chipKey);
    // Don't refetch count on every chip removal — set to null to indicate "recount"
    setResult({ ...result, filters: nextFilters, labels: nextLabels, count: -1, relaxed: null });
  };

  const applyRelaxation = () => {
    if (!result?.relaxed) return;
    const nextFilters = { ...result.filters };
    delete nextFilters[result.relaxed.droppedKey];
    if (result.relaxed.droppedKey === "locationCity") delete nextFilters.radiusKm;
    goToResults(nextFilters);
  };

  const reset = () => {
    setResult(null);
    setQuery("");
  };

  // Filter suggestions by current input prefix
  const suggestionMatches = query.trim().length > 0
    ? SUGGESTIONS.filter((s) => s.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 4)
    : SUGGESTIONS.slice(0, 5);

  const showSuggestions = focused && !result && !loading && suggestionMatches.length > 0;

  return (
    <div ref={containerRef} className="w-full relative">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent pointer-events-none" />
          <Input
            aria-label="Caută mașini descriind ce vrei în limbaj natural"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (result) setResult(null);
            }}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
              if (e.key === "Escape") setFocused(false);
            }}
            placeholder={placeholder ?? "Sau descrie ce cauți: „SUV diesel sub 15.000€, Cluj, an 2018+”"}
            className={`pl-9 ${compact ? "h-10" : "h-11"} bg-background/95`}
            disabled={loading}
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className={compact ? "h-10" : "h-11"}
          variant="secondary"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Caută cu AI"}
        </Button>
      </div>

      {!compact && !result && (
        <p className="text-[11px] text-muted-foreground mt-1.5">
          ✨ AI completează automat filtrele avansate pe baza descrierii tale
        </p>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/50">
            Exemple populare
          </div>
          {suggestionMatches.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQuery(s);
                setFocused(false);
                void runParse(s);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
            >
              <Sparkles className="h-3 w-3 text-accent shrink-0" />
              <span className="truncate">{s}</span>
            </button>
          ))}
        </div>
      )}

      {/* Preview card with editable chips */}
      {result && (
        <div className="mt-3 rounded-lg border border-border bg-card p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground">
              <Sparkles className="inline h-3 w-3 text-accent mr-1" />
              Am înțeles căutarea ta ca:
            </div>
            <button
              onClick={reset}
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" /> Modifică
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {result.labels.map((l) => (
              <Badge
                key={l.key}
                variant="secondary"
                className="pl-2.5 pr-1 py-1 gap-1 text-xs font-normal"
              >
                {l.label}
                <button
                  onClick={() => removeChip(l.key)}
                  className="ml-0.5 hover:bg-background/50 rounded-sm p-0.5"
                  aria-label={`Elimină ${l.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>

          {/* Result count + primary CTA */}
          {result.count === 0 && result.relaxed ? (
            <div className="space-y-2 pt-1 border-t border-border/50">
              <p className="text-xs text-muted-foreground">
                Nu am găsit rezultate exacte. Dacă renunț la <strong className="text-foreground">{result.relaxed.droppedLabel}</strong>, apar{" "}
                <strong className="text-foreground">{result.relaxed.newCount}</strong> mașini.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={applyRelaxation} className="flex-1">
                  Vezi {result.relaxed.newCount} mașini <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => goToResults(result.filters)}>
                  Setează alertă
                </Button>
              </div>
            </div>
          ) : result.count === 0 ? (
            <div className="pt-1 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">
                Nu am găsit rezultate pentru toate filtrele.
              </p>
              <Button size="sm" variant="outline" onClick={reset} className="w-full">
                Încearcă altă căutare
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={() => goToResults(result.filters)}
              className="w-full"
            >
              Vezi {result.count === -1 ? "" : result.count} {result.count === 1 ? "mașină" : "mașini"}
              <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
